import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Button, Icon, Label, Menu, Segment } from 'semantic-ui-react'
import ExperienceCard from './ExperienceCard'
import './ExperienceTabPanel.css'

const TAB_WORK = 0
const TAB_EDUCATION = 1
const TAB_CERTIFICATION = 2

const TAB_CONFIG = [
  { index: TAB_WORK, label: 'Work Experience', tabId: 'exp-tab-work', panelId: 'exp-tabpanel-work', emptyMessage: 'No work experiences added yet.' },
  { index: TAB_EDUCATION, label: 'Education', tabId: 'exp-tab-education', panelId: 'exp-tabpanel-education', emptyMessage: 'No education records added yet.' },
  { index: TAB_CERTIFICATION, label: 'Certification', tabId: 'exp-tab-certification', panelId: 'exp-tabpanel-certification', emptyMessage: 'No certifications added yet.' },
]

const TAB_COUNT = TAB_CONFIG.length

/**
 * Formats a date range string from item fields.
 * Handles missing values gracefully by returning partial or empty strings.
 * @param {string|undefined|null} formatStart - Formatted start date
 * @param {string|undefined|null} formatEnd - Formatted end date
 * @param {number} [isActive] - 1 if currently active (work experience only)
 * @returns {string} Date range string
 */
function formatDateRange(formatStart, formatEnd, isActive) {
  const start = (formatStart != null && typeof formatStart === 'string') ? formatStart : ''
  const end = isActive === 1 ? 'Present' : ((formatEnd != null && typeof formatEnd === 'string') ? formatEnd : '')
  if (!start && !end) return ''
  if (!start) return end
  if (!end) return start
  return start + ' \u2013 ' + end
}

/**
 * Maps a work experience item to ExperienceCard props.
 * @param {Object} item - Raw work experience data
 * @returns {Object} Props for ExperienceCard
 */
function mapWorkItem(item) {
  const title = item.role || item.business_title || ''
  const dateRange = formatDateRange(item.format_start, item.format_end, item.is_active)
  const subtitle = [item.customer, item.project_title].filter(Boolean).join(' \u00b7 ')
  const badge = item.sector || ''
  const visibleOnCv = item.visible_on_cv === 'Yes' || item.visible_on_cv === 1
  return { title, dateRange, subtitle, badge, visibleOnCv }
}

/**
 * Maps an education item to ExperienceCard props.
 * @param {Object} item - Raw education data
 * @returns {Object} Props for ExperienceCard
 */
function mapEducationItem(item) {
  const title = item.degree || ''
  const dateRange = formatDateRange(item.format_start, item.format_end)
  const parts = [item.university, item.discipline]
  if (item.vote) parts.push('Vote: ' + item.vote)
  const subtitle = parts.filter(Boolean).join(' \u00b7 ')
  return { title, dateRange, subtitle }
}

/**
 * Maps a certification item to ExperienceCard props.
 * @param {Object} item - Raw certification data
 * @returns {Object} Props for ExperienceCard
 */
function mapCertificationItem(item) {
  const title = item.type_of_certification || ''
  const dateRange = (item.format_start != null && typeof item.format_start === 'string') ? item.format_start : ''
  return { title, dateRange }
}

/**
 * Generic list renderer for experience items.
 * Renders a list of ExperienceCard components by applying a mapper function
 * to each item to derive the card props.
 *
 * Props:
 *   items      {Array}     Array of raw data items
 *   editable   {boolean}   Whether Edit buttons are shown
 *   onEdit     {Function}  [optional] Callback invoked on edit. Signature: fn(index)
 *   mapItem    {Function}  Maps a raw item to ExperienceCard props
 */
const ExperienceList = ({ items, editable, onEdit, mapItem }) => {
  if (!items || items.length === 0) return null

  return (
    <div className="exp-tab-list">
      {items.map((item, index) => {
        const cardProps = mapItem(item)
        return (
          <ExperienceCard
            key={item.id || index}
            {...cardProps}
            editable={editable}
            onEdit={onEdit ? () => onEdit(index) : undefined}
          />
        )
      })}
    </div>
  )
}

ExperienceList.propTypes = {
  items: PropTypes.array.isRequired,
  editable: PropTypes.bool.isRequired,
  onEdit: PropTypes.func,
  mapItem: PropTypes.func.isRequired,
}

/**
 * Tab panel for Work Experience, Education, and Certification sections of the CV.
 * Implements the WAI-ARIA Tabs pattern with roving tabindex and arrow key navigation.
 *
 * Props:
 *   editable                    {boolean}  Whether the user can add/edit items
 *   userWorkExperienceOptions   {Array}    Work experience items
 *   userEducationOptions        {Array}    Education items
 *   userCertificationOptions    {Array}    Certification items
 *   onAddWork                   {Function} Callback to open Add Work Experience modal
 *   onAddEducation              {Function} Callback to open Add Education modal
 *   onAddCertification          {Function} Callback to open Add Certification modal
 *   onEditWork                  {Function} [optional] Callback to open Edit Work Experience modal. Signature: fn(index)
 *   onEditEducation             {Function} [optional] Callback to open Edit Education modal. Signature: fn(index)
 *   onEditCertification         {Function} [optional] Callback to open Edit Certification modal. Signature: fn(index)
 */
const ExperienceTabPanel = ({
  editable,
  userWorkExperienceOptions,
  userEducationOptions,
  userCertificationOptions,
  onAddWork,
  onAddEducation,
  onAddCertification,
  onEditWork,
  onEditEducation,
  onEditCertification,
}) => {
  const [activeTab, setActiveTab] = useState(TAB_WORK)

  const handleTabClick = useCallback((_e, data) => {
    const idx = data.index
    if (idx >= 0 && idx < TAB_COUNT) {
      setActiveTab(idx)
    }
  }, [])

  const handleTabKeyDown = useCallback((e) => {
    let nextIndex = null
    if (e.key === 'ArrowRight') {
      nextIndex = (activeTab + 1) % TAB_COUNT
    } else if (e.key === 'ArrowLeft') {
      nextIndex = (activeTab - 1 + TAB_COUNT) % TAB_COUNT
    } else if (e.key === 'Home') {
      nextIndex = 0
    } else if (e.key === 'End') {
      nextIndex = TAB_COUNT - 1
    }

    if (nextIndex !== null) {
      e.preventDefault()
      setActiveTab(nextIndex)
      const tabElement = document.getElementById(TAB_CONFIG[nextIndex].tabId)
      if (tabElement) tabElement.focus()
    }
  }, [activeTab])

  const workItems = userWorkExperienceOptions || []
  const educationItems = userEducationOptions || []
  const certificationItems = userCertificationOptions || []

  const counts = [workItems.length, educationItems.length, certificationItems.length]

  const activeConfig = TAB_CONFIG[activeTab]

  const addHandlers = [onAddWork, onAddEducation, onAddCertification]

  return (
    <Segment className="exp-tab-panel">
      <Menu
        pointing
        secondary
        className="exp-tab-menu"
        role="tablist"
        aria-label="Experience tabs"
        onKeyDown={handleTabKeyDown}
      >
        {TAB_CONFIG.map((tab, index) => (
          <Menu.Item
            key={tab.tabId}
            id={tab.tabId}
            index={index}
            active={activeTab === index}
            onClick={handleTabClick}
            className="exp-tab-menu-item"
            role="tab"
            aria-selected={activeTab === index}
            aria-controls={tab.panelId}
            tabIndex={activeTab === index ? 0 : -1}
          >
            {tab.label}
            <Label circular size="mini" color="teal" className="exp-tab-badge">
              {counts[index]}
            </Label>
          </Menu.Item>
        ))}
      </Menu>

      <div
        id={activeConfig.panelId}
        className="exp-tab-content"
        role="tabpanel"
        aria-labelledby={activeConfig.tabId}
        aria-live="polite"
      >
        {editable && (
          <div className="exp-tab-header">
            <Button
              size="small"
              className="exp-tab-add-btn"
              onClick={addHandlers[activeTab]}
              aria-label="Add"
            >
              <Icon name="plus" aria-hidden="true" /> Add
            </Button>
          </div>
        )}

        {activeTab === TAB_WORK && (
          <ExperienceList items={workItems} editable={editable} onEdit={onEditWork} mapItem={mapWorkItem} />
        )}
        {activeTab === TAB_EDUCATION && (
          <ExperienceList items={educationItems} editable={editable} onEdit={onEditEducation} mapItem={mapEducationItem} />
        )}
        {activeTab === TAB_CERTIFICATION && (
          <ExperienceList items={certificationItems} editable={editable} onEdit={onEditCertification} mapItem={mapCertificationItem} />
        )}

        {counts[activeTab] === 0 && (
          <div className="exp-tab-empty">
            <Icon name="info circle" aria-hidden="true" />
            {activeConfig.emptyMessage}
          </div>
        )}
      </div>
    </Segment>
  )
}

ExperienceTabPanel.propTypes = {
  editable: PropTypes.bool.isRequired,
  userWorkExperienceOptions: PropTypes.array,
  userEducationOptions: PropTypes.array,
  userCertificationOptions: PropTypes.array,
  onAddWork: PropTypes.func.isRequired,
  onAddEducation: PropTypes.func.isRequired,
  onAddCertification: PropTypes.func.isRequired,
  onEditWork: PropTypes.func,
  onEditEducation: PropTypes.func,
  onEditCertification: PropTypes.func,
}

export default ExperienceTabPanel
